<template>
  <div class="ai">
    <div class="ai__head">
      <span class="ai__title">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8zM19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8z" /></svg>
        {{ t('mailClient.ai.title') }}
      </span>
      <button type="button" class="ai__close" :aria-label="t('mailClient.ai.close')" @click="$emit('close')">×</button>
    </div>

    <div class="ai__tasks">
      <button v-for="task in quick" :key="task" type="button" class="ai__task" :class="{ on: chosen === task }" :disabled="busy" @click="choose(task)">
        {{ t(`mailClient.ai.tasks.${task}`) }}
      </button>
    </div>

    <div v-if="chosen === 'tone'" class="ai__row">
      <UiSelect v-model="tone" :options="tones" />
    </div>
    <div v-if="chosen === 'translate'" class="ai__row">
      <UiSelect v-model="language" :options="languages" />
    </div>

    <textarea
      v-model="instructions"
      class="ai__instructions"
      rows="2"
      enterkeyhint="go"
      :placeholder="instructionsPlaceholder"
      :aria-label="t('mailClient.ai.instructionsLabel')"
      @keydown.enter.exact.prevent="go"
    />

    <div class="ai__actions">
      <span v-if="!chosen" class="ai__hint">{{ t('mailClient.ai.pick') }}</span>
      <UiButton size="sm" :loading="busy" :disabled="!chosen" @click="go">
        {{ result ? t('mailClient.ai.again') : t('mailClient.ai.run') }}
      </UiButton>
    </div>

    <p v-if="error" class="ai__error" role="alert">{{ error }}</p>

    <div v-if="busy && !result" class="ai__result ai__result--busy" aria-busy="true">
      <UiSkeleton :lines="4" />
    </div>
    <template v-if="result">
      <p class="ai__review">{{ t('mailClient.ai.review') }}</p>
      <!-- eslint-disable-next-line vue/no-v-html — cleaned twice: by the server (html.js) and here (sanitize.js) -->
      <div class="ai__result" v-html="safeResult" />
      <div class="ai__apply">
        <UiButton size="sm" @click="$emit('replace', safeResult); reset()">{{ t('mailClient.ai.replace') }}</UiButton>
        <UiButton size="sm" variant="secondary" @click="$emit('insert', safeResult); reset()">{{ t('mailClient.ai.insert') }}</UiButton>
        <UiButton size="sm" variant="ghost" @click="reset">{{ t('mailClient.ai.discard') }}</UiButton>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiSelect, UiSkeleton, errorMessage } from '@general/ui-kit'

import { mailApi } from './api'
import { sanitizeHtml } from './sanitize'

/**
 * The AI inside the composer. It proposes; the person decides.
 *
 * Every task returns a text that is SHOWN here first, with three ways out:
 * replace what is in the editor, insert it where the caret is, or discard it.
 * Nothing it writes reaches the recipient unless somebody presses «Enviar»
 * afterwards — there is no path from here to a send.
 */
const props = defineProps({
  html: { type: String, default: '' },
  messageId: { type: String, default: '' },
  context: { type: Object, default: null },
  replying: Boolean,
})
defineEmits(['replace', 'insert', 'close'])
const { t, locale } = useI18n()

const chosen = ref('')
const instructions = ref('')
const tone = ref('formal')
const language = ref('en')
const busy = ref(false)
const result = ref('')
const error = ref('')

const quick = computed(() => {
  const list = []
  if (props.replying) list.push('reply')
  list.push('draft', 'improve', 'fix', 'professional', 'direct', 'friendly', 'shorten', 'expand', 'summarize', 'tone', 'translate')
  return list
})
const tones = computed(() => ['formal', 'friendly', 'neutral', 'persuasive', 'empathetic', 'firm']
  .map(id => ({ value: t(`mailClient.ai.tones.${id}`), label: t(`mailClient.ai.tones.${id}`) })))
const languages = computed(() => ['es', 'ca', 'en', 'fr', 'de', 'it', 'pt']
  .map(id => ({ value: id, label: t(`mailClient.ai.languages.${id}`) })))
const instructionsPlaceholder = computed(() => (chosen.value === 'draft' || chosen.value === 'reply'
  ? t('mailClient.ai.instructionsDraft')
  : t('mailClient.ai.instructionsOther')))
const safeResult = computed(() => sanitizeHtml(result.value))

function choose(task) {
  chosen.value = task
  error.value = ''
  if (tones.value.length && !tones.value.some(option => option.value === tone.value)) tone.value = tones.value[0].value
}

function reset() {
  result.value = ''
  error.value = ''
}

async function go() {
  if (!chosen.value || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const answer = await mailApi.ai({
      task: chosen.value,
      html: props.html,
      instructions: instructions.value,
      tone: tone.value,
      language: language.value,
      messageId: props.messageId || undefined,
      context: props.context || undefined,
      locale: locale.value,
    })
    result.value = answer.html || ''
    if (!result.value) error.value = t('mailClient.ai.empty')
  } catch (failure) {
    error.value = errorMessage(failure, t)
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.ai {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: 1px solid var(--color-primary-light, var(--color-border));
  border-radius: var(--radius-sm);
  background: var(--color-primary-soft);
}
.ai__head { display: flex; align-items: center; justify-content: space-between; }
.ai__title { display: inline-flex; align-items: center; gap: 6px; font-weight: 600; font-size: var(--text-sm); color: var(--color-primary); }
.ai__title svg { width: 16px; height: 16px; fill: currentColor; }
.ai__close { border: 0; background: none; font-size: 18px; line-height: 1; cursor: pointer; color: var(--color-text-soft); }
.ai__tasks { display: flex; flex-wrap: wrap; gap: 6px; }
.ai__task {
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-text);
  padding: 3px 10px;
  font-size: var(--text-xs);
  cursor: pointer;
}
.ai__task:hover { border-color: var(--color-primary); }
.ai__task.on { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-text-on-primary); }
.ai__task:disabled { opacity: .6; cursor: default; }
.ai__row { max-width: 240px; }
.ai__instructions {
  width: 100%;
  resize: vertical;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 6px 8px;
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
}
.ai__actions { display: flex; align-items: center; justify-content: flex-end; gap: var(--space-2); }
.ai__hint { margin-right: auto; font-size: var(--text-xs); color: var(--color-text-soft); }
.ai__error { margin: 0; color: var(--color-danger); font-size: var(--text-sm); }
.ai__review { margin: 0; font-size: var(--text-xs); color: var(--color-text-soft); }
.ai__result {
  max-height: 260px;
  overflow-y: auto;
  padding: var(--space-3);
  border: 1px dashed var(--color-primary);
  border-radius: 6px;
  background: var(--color-surface);
  font: 14px/1.5 Arial, Helvetica, sans-serif;
  color: var(--color-text);
}
.ai__result--busy { border-style: solid; border-color: var(--color-border); }
.ai__apply { display: flex; flex-wrap: wrap; gap: var(--space-2); }

/* Con el dedo: las tareas a 36 px de alto, la cruz a 44 y la caja a 16 px
   (con menos, iOS amplía la página al enfocarla y no la devuelve). */
@media (pointer: coarse) {
  .ai__task { min-height: 36px; padding: 0 12px; font-size: var(--text-sm); }
  .ai__close { min-width: 44px; min-height: 44px; margin: -10px -10px -10px 0; }
  .ai__instructions { font-size: var(--text-field, 16px); }
  .ai__row { max-width: none; }
}
</style>
